import {
  ExistingSystemComponentType,
  ExistingSystemType,
  SystemOptionConfigurationType
} from 'data/types';

export type ExistingEquipmentSizeType = {
  coolingTons: number | null;
  heatingBTU: number | null;
};

export type ResolveQuoteExistingEquipmentSizeParams = {
  configuration: Pick<SystemOptionConfigurationType, 'condensers'>;
  existingSystem: ExistingSystemType | null;
};

export type CombineExistingEquipmentSizesParams = {
  configuration: Pick<SystemOptionConfigurationType, 'condensers'>;
  existingSystems: (ExistingSystemType | null)[];
};

const BTU_PER_TON = 12000;

type ComponentTypeName = ExistingSystemComponentType['type'];

const COOLING_UNIT_TYPES: ReadonlySet<ComponentTypeName> =
  new Set<ComponentTypeName>([
    'Split System Condenser',
    'Split System Heat Pump',
    'Next-Gen Condenser',
    'Next-Gen Heat Pump',
    'Self Contained Electric AC',
    'Self Contained Electric Heat Pump',
    'Self Contained Gas Pack With AC',
    'Self Contained Gas Pack With Heat Pump'
  ]);

const HEAT_PUMP_TYPES: ReadonlySet<ComponentTypeName> =
  new Set<ComponentTypeName>([
    'Split System Heat Pump',
    'Next-Gen Heat Pump',
    'Self Contained Electric Heat Pump',
    'Self Contained Gas Pack With Heat Pump'
  ]);

function readPositiveSpec(
  component: ExistingSystemComponentType,
  key: 'tonnage' | 'coolingCapacity' | 'heatingCapacity'
): number | null {
  const raw = (component as unknown as Record<string, unknown>)[key];
  if (raw == null || raw === '') return null;
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? value : null;
}

function componentCoolingTons(
  component: ExistingSystemComponentType
): number | null {
  if (!COOLING_UNIT_TYPES.has(component.type)) return null;
  const tonnage = readPositiveSpec(component, 'tonnage');
  if (tonnage !== null) return tonnage;
  const coolingCapacity = readPositiveSpec(component, 'coolingCapacity');
  return coolingCapacity === null
    ? null
    : Math.round((coolingCapacity / BTU_PER_TON) * 2) / 2;
}

function componentHeatingBtu(
  component: ExistingSystemComponentType
): number | null {
  const heatingCapacity = readPositiveSpec(component, 'heatingCapacity');
  if (heatingCapacity !== null) return heatingCapacity;
  if (!HEAT_PUMP_TYPES.has(component.type)) return null;
  const tonnage = readPositiveSpec(component, 'tonnage');
  return tonnage === null ? null : tonnage * BTU_PER_TON;
}

function largest(values: Array<number | null>): number | null {
  const known = values.filter((value): value is number => value !== null);
  return known.length === 0 ? null : Math.max(...known);
}

export function deriveExistingEquipmentSize(
  existingSystem: ExistingSystemType | null
): ExistingEquipmentSizeType | null {
  if (!existingSystem) return null;
  const coolingTons = largest(
    existingSystem.components.map(componentCoolingTons)
  );
  const heatingBTU = largest(
    existingSystem.components.map(componentHeatingBtu)
  );
  if (coolingTons === null && heatingBTU === null) return null;
  return { coolingTons, heatingBTU };
}

function hasDuctedZone(
  configuration: Pick<SystemOptionConfigurationType, 'condensers'>
): boolean {
  return configuration.condensers.some((condenser) =>
    condenser.zones.some((zone) => zone.type === 'ducted')
  );
}

export function resolveQuoteExistingEquipmentSize({
  configuration,
  existingSystem
}: ResolveQuoteExistingEquipmentSizeParams): ExistingEquipmentSizeType | null {
  if (!hasDuctedZone(configuration)) return null;
  return deriveExistingEquipmentSize(existingSystem);
}

function sumKnown(values: Array<number | null>): number | null {
  const known = values.filter((value): value is number => value !== null);
  return known.length === 0
    ? null
    : known.reduce((total, value) => total + value, 0);
}

export function resolveQuoteCombinedExistingEquipmentSize({
  configuration,
  existingSystems
}: CombineExistingEquipmentSizesParams): ExistingEquipmentSizeType | null {
  const distinct = existingSystems.filter(
    (existingSystem, index, all) =>
      all.findIndex(
        (candidate) => candidate?.uniqueId === existingSystem?.uniqueId
      ) === index
  );
  const sizes = distinct
    .map((existingSystem) =>
      resolveQuoteExistingEquipmentSize({ configuration, existingSystem })
    )
    .filter((size): size is ExistingEquipmentSizeType => size !== null);
  if (sizes.length === 0) return null;
  if (sizes.length === 1) return sizes[0];
  return {
    coolingTons: sumKnown(sizes.map((size) => size.coolingTons)),
    heatingBTU: sumKnown(sizes.map((size) => size.heatingBTU))
  };
}
